import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import ScoreGauge from "@/components/ScoreGauge";
import IntentCoverageCard from "@/components/IntentCoverageCard";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LanguageContext";
import { limitsFor } from "@/lib/plan";
import { Loader2, Info, ArrowRight, Lightbulb, CheckCircle2, XCircle, AlertTriangle, ShieldCheck } from "lucide-react";

const SUB_KEYS = [
  "anlasilabilirlik", "bulunabilirlik", "onerilebilirlik", "icerik_gucu",
  "guven_sinyali", "lokasyon_netligi", "hizmet_netligi", "niyet_kapsami",
];

export default function AIScore() {
  const { id } = useParams();
  const { user } = useAuth();
  const { lang, t } = useLang();
  const limits = limitsFor(user);
  const intentsVisible = limits.intents_visible ?? 2;
  const [scan, setScan] = useState(null);
  const [biz, setBiz] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get(`/businesses/${id}`).then((r) => r.data),
      api.get(`/scans/${id}/latest`, { params: { lang } }).then((r) => r.data).catch(() => null),
    ]).then(([b, s]) => { setBiz(b); setScan(s); setLoading(false); });
  }, [id, lang]);

  if (loading) return <div className="text-slate-400 flex items-center"><Loader2 className="animate-spin mr-2" /> {t("common.loading")}</div>;
  if (!scan) {
    return (
      <div className="glass-strong rounded-3xl p-10 text-center">
        <h2 className="font-display text-2xl font-semibold text-white mb-2">{t("dash.noScan.title")}</h2>
        <p className="text-slate-400 mb-6">{t("dash.noScan.desc")}</p>
        <Link to={`/app/isletme/${id}/analiz`} className="neon-btn rounded-xl px-6 py-3 text-white font-semibold inline-flex items-center gap-2">
          {t("dash.noScan.cta")} <ArrowRight size={16} />
        </Link>
      </div>
    );
  }

  const explanations = scan.subscore_explanations || {};
  const next_steps = scan.next_steps || [];
  const fetched = scan.fetched || {};
  const tech = scan.technical_signals || {};
  const robots = tech.robots || {};

  const signals = [
    {
      key: "faq",
      label: t("score.signal.faq"),
      ok: !!fetched.has_faq,
      hint: fetched.has_faq ? t("score.signal.faq.ok") : t("score.signal.faq.bad"),
    },
    {
      key: "about",
      label: t("score.signal.about"),
      ok: !!fetched.has_about,
      hint: fetched.has_about ? t("score.signal.about.ok") : t("score.signal.about.bad"),
    },
    {
      key: "services",
      label: t("score.signal.services"),
      ok: !!fetched.has_services,
      hint: fetched.has_services ? t("score.signal.services.ok") : t("score.signal.services.bad"),
    },
    {
      key: "location",
      label: t("score.signal.location"),
      ok: !!(fetched.address_found || biz?.city),
      hint: (fetched.address_found || biz?.city) ? t("score.signal.location.ok") : t("score.signal.location.bad"),
    },
    {
      key: "contact",
      label: t("score.signal.contact"),
      ok: !!(fetched.has_contact || fetched.phone_found),
      hint: (fetched.has_contact || fetched.phone_found) ? t("score.signal.contact.ok") : t("score.signal.contact.bad"),
    },
    {
      key: "schema",
      label: t("score.signal.schema"),
      ok: !!tech.has_schema,
      hint: tech.has_schema
        ? t("score.signal.schema.ok", { types: (tech.schema_types || []).join(", ") || t("score.signal.schema.typesUnknown") })
        : t("score.signal.schema.bad"),
    },
    {
      key: "robots",
      label: t("score.signal.robots"),
      ok: robots.site_open !== false && !robots.important_paths_blocked && (robots.ai_bots_blocked || []).length === 0,
      hint: robots.status_message || "—",
      warn: robots.found === false,
    },
    {
      key: "google_desc",
      label: t("score.signal.googleDesc"),
      ok: !!(biz?.description && biz.description.length > 60),
      hint: (biz?.description && biz.description.length > 60) ? t("score.signal.googleDesc.ok") : t("score.signal.googleDesc.bad"),
    },
  ];

  return (
    <div className="space-y-6" data-testid="ai-score-page">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-cyan-400 mb-2">{t("score.eyebrow")}</div>
        <h1 className="font-display text-4xl font-bold text-white">{biz?.name}</h1>
        <div className="mt-4 rounded-xl bg-amber-500/5 border border-amber-500/20 p-4 text-sm text-amber-100 leading-relaxed">
          <strong className="text-amber-300">{t("score.caveat.important")}</strong> {t("score.caveat.body")}
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        <div className="lg:col-span-1 glass-strong rounded-3xl p-7 flex flex-col items-center justify-center">
          <ScoreGauge score={scan.overall_score} label={t("dash.overallScoreLabel")} />
          <p className="text-sm text-slate-400 mt-5 text-center leading-relaxed">{scan.score_explanation}</p>
        </div>

        <div className="lg:col-span-2 glass rounded-3xl p-7">
          <div className="text-xs uppercase tracking-wider text-slate-400 mb-5">{t("score.subscoresTitle")}</div>
          <div className="space-y-5">
            {Object.entries(scan.subscores || {}).map(([k, v]) => {
              const known = SUB_KEYS.includes(k);
              const label = known ? t(`score.sub.${k}.label`) : k;
              const desc = known ? t(`score.sub.${k}.desc`) : "";
              const exp = explanations[k];
              const safe = Math.max(0, Math.min(100, v || 0));
              const color = safe >= 70 ? "from-emerald-400 to-cyan-400"
                : safe >= 40 ? "from-amber-400 to-orange-500"
                : "from-red-500 to-pink-500";
              return (
                <div key={k} data-testid={`subscore-${k}`}>
                  <div className="flex items-center justify-between mb-1.5">
                    <div>
                      <div className="text-white font-medium">{label}</div>
                      <div className="text-xs text-slate-500">{desc}</div>
                    </div>
                    <span className="font-mono text-lg text-white">{safe}</span>
                  </div>
                  <div className="h-2 rounded-full bg-white/5 overflow-hidden mb-2">
                    <div className={`h-full bg-gradient-to-r ${color}`} style={{ width: `${safe}%`, transition: "width 1s ease" }} />
                  </div>
                  {exp && (
                    <div className="flex gap-2 rounded-lg bg-white/[0.02] border border-white/5 p-3 text-xs text-slate-300">
                      <Info size={13} className="text-cyan-400 mt-0.5 shrink-0" />
                      <span>{exp}</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <IntentCoverageCard
        coverage={scan.intent_coverage || []}
        score={scan.intent_score ?? scan.subscores?.niyet_kapsami ?? null}
        visibleCap={intentsVisible}
      />

      <div className="glass-strong rounded-3xl p-7" data-testid="visibility-signals-panel">
        <div className="flex items-center gap-2 mb-1">
          <ShieldCheck size={18} className="text-cyan-300" />
          <h3 className="font-display text-xl font-semibold text-white">{t("score.signalsTitle")}</h3>
        </div>
        <p className="text-xs text-slate-400 mb-5">{t("score.signalsDesc")}</p>
        <div className="grid sm:grid-cols-2 gap-3">
          {signals.map((s) => {
            const Icon = s.ok ? CheckCircle2 : (s.warn ? AlertTriangle : XCircle);
            const accent = s.ok
              ? "text-emerald-400 bg-emerald-500/5 border-emerald-500/15"
              : s.warn
              ? "text-amber-300 bg-amber-500/5 border-amber-500/20"
              : "text-red-300 bg-red-500/5 border-red-500/15";
            return (
              <div
                key={s.key}
                className={`rounded-xl border p-3 flex gap-3 items-start ${accent}`}
                data-testid={`signal-${s.key}`}
              >
                <Icon size={16} className="mt-0.5 shrink-0" />
                <div className="min-w-0">
                  <div className="text-sm font-medium text-white leading-snug">{s.label}</div>
                  <div className="text-xs text-slate-300 mt-1 leading-relaxed">{s.hint}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {next_steps.length > 0 && (
        <div className="glass-strong rounded-3xl p-7">
          <div className="flex items-center gap-2 mb-4">
            <Lightbulb size={18} className="text-amber-300" />
            <h3 className="font-display text-xl font-semibold text-white">{t("score.nextSteps")}</h3>
          </div>
          <ol className="space-y-3">
            {next_steps.map((step, i) => (
              <li key={i} className="flex gap-3 items-start">
                <span className="w-7 h-7 rounded-full bg-gradient-to-br from-cyan-500/20 to-purple-500/20 border border-white/10 flex items-center justify-center text-xs font-mono text-cyan-300 shrink-0">
                  {i + 1}
                </span>
                <span className="text-sm text-slate-200 leading-relaxed pt-1">{step}</span>
              </li>
            ))}
          </ol>
          <div className="mt-5 flex flex-wrap gap-2">
            <Link to={`/app/isletme/${id}/gorevler`} className="neon-btn rounded-lg px-4 py-2 text-sm text-white font-medium inline-flex items-center gap-1.5">
              {t("dash.goToTasks")} <ArrowRight size={14} />
            </Link>
            <Link to={`/app/isletme/${id}/icerik`} className="ghost-btn rounded-lg px-4 py-2 text-sm text-white">
              {t("score.contentGen")}
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
